import { dashboardApi } from '@/store/api/base/api.base';
import { walkPages } from '@/store/api/base/api.walk';
import { writeKey } from '@/store/api/begateway/begateway.keys.cache';
import type { TypeHygieneReport } from '@/types/begateway/types.begateway.hygiene';
import type { TypeApiKey, TypeApiKeyInput, TypeApiKeyMinted } from '@/types/begateway/types.begateway.key';

const PAGE_SIZE = 200;

const MAX_PAGES = 50;

export const byCreated = (a: TypeApiKey, b: TypeApiKey) => b.audit.createdAt.localeCompare(a.audit.createdAt) || b.id.localeCompare(a.id);

export const begatewayKeysApi = dashboardApi.injectEndpoints({
    endpoints: (build) => ({
        listGatewayKeys: build.query<TypeApiKey[], void>({
            queryFn: async () => {
                const walked = await walkPages<TypeApiKey>({
                    url: '/begateway/keys',
                    fallback: 'Non è stato possibile leggere le chiavi dal gateway',
                    pageSize: PAGE_SIZE,
                    maxPages: MAX_PAGES,
                });
                if ('error' in walked) return walked;
                const items = walked.data;

                items.sort(byCreated);
                return { data: items };
            },
            providesTags: ['GatewayKey'],
        }),

        readGatewayKey: build.query<TypeApiKey, string>({
            query: (keyId) => ({ url: `/begateway/keys/${keyId}` }),
            providesTags: (_result, _error, keyId) => [{ type: 'GatewayKey', id: keyId }],
        }),

        readGatewayHygiene: build.query<TypeHygieneReport, void>({
            query: () => ({ url: '/begateway/keys/hygiene' }),
            providesTags: ['GatewayKey'],
        }),

        createGatewayKey: build.mutation<TypeApiKeyMinted, TypeApiKeyInput>({
            query: (apiKey) => ({ url: '/begateway/keys', method: 'post', data: { apiKey } }),
            invalidatesTags: (result) => (result ? ['GatewayKey', 'GatewayService'] : []),
        }),

        updateGatewayKey: build.mutation<TypeApiKey, { keyId: string; apiKey: TypeApiKeyInput }>({
            query: ({ keyId, apiKey }) => ({ url: `/begateway/keys/${keyId}`, method: 'patch', data: { apiKey } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeKey(dispatch, data);
                } catch {
                }
            },
        }),

        rotateGatewayKey: build.mutation<TypeApiKeyMinted, { keyId: string; graceHours: number; ttlDays?: number | null }>({
            query: ({ keyId, graceHours, ttlDays }) => ({ url: `/begateway/keys/${keyId}/rotate`, method: 'post', data: { graceHours, ...(ttlDays ? { ttlDays } : {}) } }),
            invalidatesTags: (result) => (result ? ['GatewayKey', 'GatewayService'] : []),
        }),

        revokeGatewayKey: build.mutation<TypeApiKey, { keyId: string; reason: string }>({
            query: ({ keyId, reason }) => ({ url: `/begateway/keys/${keyId}/revoke`, method: 'post', data: { reason } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeKey(dispatch, data);
                    dispatch(begatewayKeysApi.util.invalidateTags(['GatewayService']));
                } catch {
                }
            },
        }),
    }),
});

export const {
    useListGatewayKeysQuery,
    useReadGatewayKeyQuery,
    useReadGatewayHygieneQuery,
    useCreateGatewayKeyMutation,
    useUpdateGatewayKeyMutation,
    useRotateGatewayKeyMutation,
    useRevokeGatewayKeyMutation,
} = begatewayKeysApi;

export default begatewayKeysApi;
